import React from 'react'
import {useEventBusChannel} from './EventBus'
import {SaveableStateEvents} from './saveableStateEvents'
import {useViewLoader} from './ViewLoader/ViewLoadContext'

// TODO merge useRegisterSaveableState and useRegisterDirtyableState

interface SaveableState {
    canEdit?: boolean
    save: () => Promise<void> | void
    discard: () => Promise<void> | void
}

export function useRegisterSaveableState(state: SaveableState, savePriority = 0, discardPriority = 0): void {
    useRegisterSaveDiscard(state.save, state.discard, state.canEdit, savePriority, discardPriority)
}

export function useRegisterSaveDiscard(
    save: () => Promise<void> | void,
    discard: () => Promise<void> | void,
    canEdit = true,
    savePriority = 0,
    discardPriority = 0
): void {
    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()
    eventBusChannel.useEvent('save', canEdit ? save : null, savePriority)
    eventBusChannel.useEvent('discard', canEdit ? discard : null, discardPriority)
}

export function useGlobalSave(showLoader = false): () => Promise<void> {
    const loader = useViewLoader()
    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()

    const save = React.useCallback(async () => {
        let success = true
        await eventBusChannel.trigger('preSave', {})
        try {
            await eventBusChannel.trigger('save', {})
        } catch {
            success = false
        } finally {
            await eventBusChannel.trigger('saveFinished', {success: success})
        }
    }, [eventBusChannel])

    return React.useCallback(async () => {
        if (showLoader) {
            await loader.whileLoading(save)
        } else {
            await save()
        }
    }, [showLoader, loader, save])
}

export function useGlobalDiscard(showLoader = false): () => Promise<void> {
    const loader = useViewLoader()
    const eventBusChannel = useEventBusChannel<SaveableStateEvents>()

    return React.useCallback(async () => {
        if (showLoader) {
            await loader.whileLoading(eventBusChannel.trigger('discard', {}))
        } else {
            await eventBusChannel.trigger('discard', {})
        }
    }, [showLoader, loader, eventBusChannel])
}
